// Step 2, Achieved so far: every Amazon building, sorted by committed COD, against the
// work under way and the big blocks on the path. Hovering a cell says what it shows. Step
// names and owners are Step 1's, as edited there.
import { Fragment, type ReactNode, useContext, useMemo } from "react";

import type { AmazonBuilding } from "../../api/hooks/useAmazonOverview";
import { UserContext } from "../../auth";
import { ReviewProvider } from "../../components/ReviewProvider";
import type { Tone as GapTone } from "../../utils/sizeGap";
import type { StoryStep } from "./blueprint";
import { TOP } from "./BuildingView";
import { type Cell, grantedCapacity, viableCapacity } from "./cells";
import { CellCard } from "./CellCard";
import { namer, type Shown, shownPart, TONE_CLASS } from "./shownPart";
import { day, formatDate, uncountedYears } from "./dates";
import { EditLayout, EditPane } from "./EditPane";
import { markOf, type Target, useEdit } from "./editContext";
import { Editable } from "./editing";
import { HoverCard } from "./HoverCard";
import { buildingKey, cellKeys, type Overrides } from "./overrides";
import { type Owner } from "./pathSteps";
import { PartyChip } from "./PartyChip";
import { RoofProcessLink } from "./RoofProcess";
import { DEFAULT_BUFFER_DAYS, neededBy, programmeLinks } from "./schedule";
import { mwText, ppaText } from "./sizes";
import styles from "./amazonOverview.module.css";

interface Column {
  key: string;
  /** The column's own name; absent where it is its one step's name on Step 1. */
  title?: string;
  sub?: string;
  unapproved?: boolean;
  /** The steps in the cell, in the order they happen. */
  parts: string[];
}

const COLUMNS: Column[] = [
  { key: "gridapp", parts: ["gridapp"] },
  { key: "gridoffer", parts: ["gridoffer"] },
  {
    key: "structural",
    title: "Structural",
    sub: "Assessment • Sign-off",
    parts: ["structural", "signoff"],
  },
  {
    key: "llpack",
    title: "Landlord consent pack",
    sub: "To Amazon • Sent • Feedback",
    parts: ["llpack", "llsent", "llfeedback"],
  },
  { key: "pcg", parts: ["pcg"] },
  { key: "inputs", parts: ["inputs"] },
  { key: "roof", unapproved: true, parts: ["roof"] },
  { key: "dr3", parts: ["dr3"] },
  { key: "install", title: "Install started", parts: ["install"] },
];

/** A headline tile: its count, and for a size it sums, that sum and what it leaves out. */
interface Tile {
  value: string;
  label: string;
  detail?: string;
  note?: string;
  ids: string[];
}

/** Where the work under way ends and the big blocks on the path begin. */
const SPLIT_AT = 5;

/** A split cell's part: its owner's colour on the left, rounded at the cell's ends. */
const partClass = (owner: Owner, index: number, count: number): string => {
  if (index === 0) return styles.halfOurs;
  const last = index === count - 1;
  if (owner === "third") return last ? styles.halfLandlord : styles.halfMiddle;
  return last ? styles.halfAmazonCell : styles.halfMiddle;
};

/** What a step's mark says, in edit mode's hover card. */
const MARK_NOTE = {
  set: "written over by hand.",
  drift: "written over by hand, and the platform has changed since.",
} as const;

const GAP_DOT: Record<GapTone, string> = { amber: styles.gapAmber, red: styles.gapRed };

const cellText = (cell: Cell) => (
  <>
    {!cell.sub && cell.gap ? (
      <span className={styles.cellTextGap}>
        <span>{cell.text}</span>
        <i className={GAP_DOT[cell.gap]} aria-hidden="true" />
      </span>
    ) : (
      cell.text
    )}
    {cell.sub && (
      <span className={cell.gap ? styles.cellSubGap : styles.cellSub}>
        <span>{cell.sub}</span>
        {cell.gap && <i className={GAP_DOT[cell.gap]} aria-hidden="true" />}
      </span>
    )}
  </>
);

/** One cell and its hover card. In edit mode the card still opens on hover, and a click
 * on one of the cell's steps opens that step in the pane. */
const CellBody = ({
  b,
  parts,
  split,
}: {
  b: AmazonBuilding;
  parts: Shown[];
  split: boolean;
}) => {
  const edit = useEdit();
  const half = (p: Shown, j: number, inner: ReactNode) =>
    split ? (
      <span
        key={p.id}
        className={`${styles.splitHalf} ${TONE_CLASS[p.cell.tone]} ${partClass(p.step.owner, j, parts.length)}`}
      >
        {inner}
      </span>
    ) : (
      <Fragment key={p.id}>{inner}</Fragment>
    );
  const target = (p: Shown): Target => ({ kind: "step", projectId: b.project_id, stepId: p.id });
  return (
    <HoverCard
      className={split ? styles.splitButton : styles.cellButton}
      label={`${b.site_code}: ${parts
        .map((p) => `${p.cell.shownAs?.title ?? p.step.title}: ${p.cell.status}`)
        .join("; ")}`}
      onClick={edit.editing ? () => edit.select(target(parts[0])) : undefined}
      review={`project:${b.project_id}`}
      trigger={parts.map((p, j) =>
        half(
          p,
          j,
          edit.editing ? (
            <Editable target={target(p)} keys={cellKeys(p.id)} untitled>
              {p.cell.text ? cellText(p.cell) : " "}
            </Editable>
          ) : (
            cellText(p.cell)
          ),
        ),
      )}
    >
      <CellCard b={b} parts={parts} />
      {edit.editing && (
        <>
          {parts.map((p) => {
            const mark = markOf(edit, b.project_id, cellKeys(p.id));
            return mark === "none" ? null : (
              <p key={p.id} className={mark === "drift" ? styles.error : styles.editYours}>
                {p.cell.shownAs?.title ?? p.step.title}: {MARK_NOTE[mark]}
              </p>
            );
          })}
          <p className={styles.cardMeans}>
            {split ? "Click a part of the cell to edit that step." : "Click the cell to edit it."}
          </p>
        </>
      )}
    </HoverCard>
  );
};

export const Heatmap = ({
  buildings,
  steps,
  onOpen,
}: {
  buildings: AmazonBuilding[];
  steps: StoryStep[];
  /** Opens the building in Step 3. */
  onOpen: (projectId: string) => void;
}) => {
  const edit = useEdit();
  const external = useContext(UserContext).role === "external";
  const named = namer(steps);
  // Each building's needed-by dates, as Step 3 works them out, so a late input reads late.
  const links = useMemo(() => programmeLinks(steps), [steps]);
  const needs = useMemo(
    () => new Map(buildings.map((b) => [b.project_id, neededBy(b, links, DEFAULT_BUFFER_DAYS)])),
    [buildings, links],
  );
  // Design approval's needed-by date counts Business Days back; a year with no bank
  // holidays served there is flagged on the building, and only on it.
  const uncounted = (b: AmazonBuilding): number[] =>
    uncountedYears([needs.get(b.project_id)?.dr3]);
  const shown = (b: AmazonBuilding, o: Overrides, id: string): Shown =>
    shownPart(b, o, id, named, needs.get(b.project_id) ?? null);
  // Out of the buildings the step applies to.
  const tally = (step: string, alsoDone?: string): string => {
    const applies = buildings.filter((b) => b.steps[step].state !== "not_applicable");
    const done = applies.filter(
      (b) =>
        b.steps[step].state === "done" ||
        (alsoDone !== undefined && b.steps[alsoDone].state === "done"),
    );
    return `${done.length}/${applies.length}`;
  };
  const count = (n: number, what: string) =>
    n ? `${n} ${what.replace("{s}", n === 1 ? "" : "s")}` : "";
  const capacity = grantedCapacity(buildings);
  const grantedLine = `${mwText(capacity.granted, "MWac")} granted of ${mwText(capacity.committed, "MWac")} committed`;
  const grantedNote = [
    count(capacity.unsized, "offer{s} with no size recorded"),
    count(capacity.uncommitted, "building{s} with no committed kWac"),
  ]
    .filter(Boolean)
    .join(" · ");
  const viable = viableCapacity(buildings);
  const viableRange =
    mwText(viable.min, "MWp") === mwText(viable.max, "MWp")
      ? mwText(viable.min, "MWp")
      : `${mwText(viable.min, "MWp").replace(" MWp", "")}-${mwText(viable.max, "MWp")}`;
  const viableLine = `${viableRange} structurally viable of ${mwText(viable.committed, "MWp")} committed (Subject to PV redesign)`;
  const viableNote = [
    count(viable.afterWorks, "building{s} viable only after works"),
    count(viable.unestimated, "building{s} with no viable kWp estimate"),
    count(viable.uncommitted, "building{s} with no committed kWp"),
  ]
    .filter(Boolean)
    .join(" · ");
  const tiles: Tile[] = [
    { value: tally("gridapp"), label: "grid applications submitted", ids: ["gridapp"] },
    {
      value: tally("gridoffer", "gridacc"),
      label: "grid offers in hand",
      detail: grantedLine,
      note: grantedNote,
      ids: ["gridoffer", "gridacc"],
    },
    {
      value: tally("structural"),
      label: "structural assessments complete",
      detail: viableLine,
      note: viableNote,
      ids: ["structural"],
    },
    {
      value: tally("llpack"),
      label: "preliminary consent packs sent to Amazon",
      ids: ["llpack"],
    },
  ];
  const tileFrom = (...ids: string[]): Target[] =>
    buildings.flatMap((b) =>
      ids.map((id): Target => ({ kind: "step", projectId: b.project_id, stepId: id })),
    );
  return (
    <EditLayout
      pane={<EditPane page="achieved" buildings={buildings} portfolio={buildings} steps={steps} wordings={[]} />}
    >
      <div className={styles.tiles}>
        {tiles.map(({ value, label, detail, note, ids }) => (
          <div key={label} className={styles.tile}>
            <div className={styles.tileValue}>
              <Editable
                target={{ kind: "computed", projectId: "", label, from: tileFrom(...ids) }}
                keys={[]}
              >
                {value}
              </Editable>
            </div>
            <div className={styles.tileLabel}>{label}</div>
            {detail && <div className={styles.tileDetail}>{detail}</div>}
            {note && <div className={styles.tileNote}>{note}</div>}
          </div>
        ))}
      </div>
      <div className={styles.legend}>
        <span>
          <i className={styles.toneDone} /> Done
        </span>
        <span>
          <i className={styles.toneOpen} /> In progress
        </span>
        <span>
          <i className={styles.toneBlocked} /> Blocked or late
        </span>
        <span>
          <i className={styles.toneNone} /> Not started
        </span>
        <span>
          <i className={styles.toneNa} /> Not applicable
        </span>
        <span>
          <span className={styles.star}>*</span> Process not approved
        </span>
      </div>
      {/* Text selected in a cell's card is a comment for that building's review
          conversation (CellCard); off while editing, where a click edits the page. */}
      <ReviewProvider
        available={!edit.editing && !external}
        defaultDoc={null}
        defaultLevel="amazon_step"
        comments={[]}
        projectId={null}
        onQuote={null}
      >
      <div className={styles.tableScroll}>
        <table className={styles.heatmap}>
          <thead>
            <tr>
              <th />
              <th className={styles.gap} />
              <th className={styles.group} colSpan={SPLIT_AT}>
                Work under way
              </th>
              <th className={styles.gap} />
              <th
                className={`${styles.group} ${styles.groupBig}`}
                colSpan={COLUMNS.length - SPLIT_AT}
              >
                Big blocks on the path
              </th>
              <th className={styles.gap} />
              <th />
            </tr>
            <tr>
              <th className={styles.siteHead}>
                Building<span className={styles.thSub}>PPA committed size</span>
              </th>
              <th className={styles.gap} />
              {COLUMNS.map((c, i) => (
                <Fragment key={c.key}>
                  {i === SPLIT_AT && <th className={styles.gap} />}
                  <th>
                    {c.title ?? (
                      <Editable target={{ kind: "stepOne", stepId: c.parts[0] }} keys={[]}>
                        {named(c.parts[0]).title}
                      </Editable>
                    )}
                    {c.unapproved && c.key !== "roof" && <span className={styles.star}>*</span>}
                    {c.key === "roof" && (
                      <span className={styles.thSub}>
                        <RoofProcessLink />
                      </span>
                    )}
                    {c.sub && <span className={styles.thSub}>{c.sub}</span>}
                    <span className={styles.thOwners}>
                      {c.parts.map((id) => {
                        const s = named(id);
                        return <PartyChip key={id} owner={s.owner} label={s.ownerLabel} />;
                      })}
                    </span>
                  </th>
                </Fragment>
              ))}
              <th className={styles.gap} />
              <th>Committed COD</th>
            </tr>
          </thead>
          <tbody>
            {buildings.map((b, row) => {
              const o = edit.overrides(b.project_id);
              return (
                <tr key={b.project_id}>
                  <td className={styles.siteCell}>
                    <Editable
                      target={{ kind: "building", projectId: b.project_id }}
                      keys={[
                        buildingKey("site_code"),
                        buildingKey("committed_kwp"),
                        buildingKey("committed_kw_ac"),
                      ]}
                      block
                    >
                      {row < TOP && !edit.editing ? (
                        <button
                          type="button"
                          className={styles.siteLink}
                          onClick={() => onOpen(b.project_id)}
                        >
                          {b.site_code}
                        </button>
                      ) : (
                        <span className={styles.siteCode}>{b.site_code}</span>
                      )}
                      <small>
                        PPA{b.steps.sppa?.state === "open" ? " (draft SPPA)" : ""}: {ppaText(b)}
                      </small>
                      {uncounted(b).length > 0 && (
                        <small className={styles.uncounted}>
                          Cannot count business days for {uncounted(b).join(", ")}
                        </small>
                      )}
                    </Editable>
                  </td>
                  <td className={styles.gap} />
                  {COLUMNS.map((c, i) => {
                    const gap = i === SPLIT_AT ? <td className={styles.gap} /> : null;
                    const parts =
                      c.key === "llpack" && b.freehold
                        ? [shown(b, o, "llsignoff")]
                        : c.parts.map((id) => shown(b, o, id));
                    const split = parts.length > 1;
                    return (
                      <Fragment key={c.key}>
                        {gap}
                        <td className={split ? styles.splitCell : TONE_CLASS[parts[0].cell.tone]}>
                          <CellBody b={b} parts={parts} split={split} />
                        </td>
                      </Fragment>
                    );
                  })}
                  <td className={styles.gap} />
                  <td className={styles.meta}>
                    <Editable
                      target={{ kind: "building", projectId: b.project_id }}
                      keys={[buildingKey("committed_cod")]}
                    >
                      {b.committed_cod ? formatDate(day(b.committed_cod)) : "-"}
                    </Editable>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </ReviewProvider>
    </EditLayout>
  );
};
